'use client'

import { Suspense } from 'react'
import Image from 'next/image'
import { createClient } from '@/lib/supabase/client'
import { useSearchParams } from 'next/navigation'
import HeroBackgroundDynamic from '@/components/marketing/HeroBackgroundDynamic'

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" className="shrink-0">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  )
}

function AppleIcon() {
  return (
    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.54 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09z"/>
      <path d="M15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.56-1.701z"/>
    </svg>
  )
}

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  exchange_failed: 'That sign-in link has expired or already been used. Please try again.',
  no_code:         'The sign-in link was incomplete. Please try again.',
}

function LoginForm() {
  const supabase       = createClient()
  const searchParams   = useSearchParams()
  const redirectIntent = searchParams.get('redirect') ?? 'auto'
  const authErrorCode  = searchParams.get('error')
  const authErrorMsg   = authErrorCode ? (AUTH_ERROR_MESSAGES[authErrorCode] ?? 'Something went wrong. Please try again.') : null

  const callbackUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/api/auth/callback?redirect=${redirectIntent}`
    : `/api/auth/callback?redirect=${redirectIntent}`

  function handleOAuth(provider: 'google' | 'apple') {
    supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: callbackUrl },
    })
  }

  return (
    <div className="flex flex-col gap-5">
      {authErrorMsg && (
        <div
          role="alert"
          className="rounded-xl px-4 py-3 text-sm"
          style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}
        >
          {authErrorMsg}
        </div>
      )}

      <div className="space-y-1">
        <h1 className="text-[22px] font-bold text-[var(--c-text-1)] tracking-tight">Welcome back.</h1>
        <p className="text-sm text-[var(--c-text-2)]">Sign in to your Chalk account.</p>
      </div>

      <div className="flex flex-col gap-3">
        {/* Google — follows Google's "Sign in with Google" branding guidelines:
            white background, #DADCE0 border, multicolor G logo, "Sign in with Google" wording.
            https://developers.google.com/identity/branding-guidelines */}
        <button
          onClick={() => handleOAuth('google')}
          className="w-full flex items-center justify-center gap-3 rounded-xl text-[14px] font-medium transition-colors"
          style={{
            minHeight: '48px',
            background: '#FFFFFF',
            border: '1px solid #DADCE0',
            color: '#1F1F1F',
            fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif',
          }}
        >
          <GoogleIcon />
          Sign in with Google
        </button>

        <button
          onClick={() => handleOAuth('apple')}
          className="w-full flex items-center justify-center gap-2.5 rounded-xl border border-[var(--c-border)] bg-[var(--c-bg-panel)] text-sm font-semibold text-[var(--c-text-1)] hover:bg-[var(--c-bg-sunken)] transition-colors"
          style={{ minHeight: '48px' }}
        >
          <AppleIcon />
          Continue with Apple
        </button>
      </div>

      <div className="flex items-center justify-center gap-3 pt-1">
        <a href="/signup" className="text-[12px] text-[var(--c-text-3)] hover:text-[var(--c-text-2)]">
          New to Chalk? Sign up
        </a>
        <span className="text-[var(--c-text-4)]">·</span>
        <a href="/login/password" className="text-[12px] text-[var(--c-text-3)] hover:text-[var(--c-text-2)]">
          Sign in with password
        </a>
      </div>
    </div>
  )
}

export default function LoginPage() {
  return (
    <div
      className="min-h-dvh flex items-center justify-center p-5"
      style={{ background: '#0C0E14', position: 'relative', overflow: 'hidden' }}
    >
      <HeroBackgroundDynamic />

      {/* Hero copy — visible only on screens wide enough to show it beside the card.
          Mirrors MarketingHero.tsx so the sign-in moment reinforces the brand promise. */}
      <div
        className="hidden lg:flex flex-col gap-5 mr-16"
        style={{ maxWidth: '360px', position: 'relative', zIndex: 1 }}
        aria-hidden="true"
      >
        {/* Award Cycle 2027 voice register. Kill list bans
         *  "Operating System for Youth Sports", "Run Your Game / Team /
         *  Club", and "one app your whole program runs on." Replaced with
         *  the v2 spike voice that scored 5/5 on the auth audit. */}
        <p
          className="animate-entry-1 text-[11px] font-bold tracking-[0.2em] uppercase"
          style={{ color: 'rgba(245,230,66,0.85)' }}
        >
          Coaching infrastructure
        </p>
        <h1
          className="animate-entry-2 text-[44px] leading-[0.98] font-black tracking-[-1.5px] text-white"
        >
          <span className="block">For the coach first.</span>
          <span className="block">
            <span
              className="font-bold tracking-normal"
              style={{ fontFamily: 'var(--font-caveat)', color: '#F5E642', fontSize: '0.88em' }}
            >
              For parents,
            </span>{' '}
            always free.
          </span>
        </h1>
        <p
          className="animate-entry-2 text-[16px] leading-relaxed"
          style={{ color: 'rgba(255,255,255,0.55)' }}
        >
          Built for coaches. Parents free, always.
        </p>
      </div>

      <div
        className="w-full rounded-2xl flex flex-col gap-0 overflow-hidden animate-entry-0"
        style={{
          maxWidth: '360px',
          background: 'var(--c-bg-panel)',
          boxShadow: 'var(--shadow-xl)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div className="flex items-center px-7 pt-7 pb-5 animate-entry-1">
          <Image
            src="/chalk-logo-transparent-black.png"
            alt="Chalk"
            width={1692}
            height={1200}
            priority
            className="h-auto w-[120px]"
          />
        </div>

        <div className="px-7 pb-8 animate-entry-2">
          <Suspense fallback={null}>
            <LoginForm />
          </Suspense>
        </div>

        {/* CTA separator line — animates in last */}
        <div className="animate-entry-3" />
      </div>
    </div>
  )
}
